import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMFA } from '../../contexts/MFAContext';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../contexts/ApiContext';
import { MFATypes } from '@flashbacktech/flashbackclient/api';
import PostLoginMFAVerification from './PostLoginMFAVerification';
import { debugAuthLog } from '../../utils/debugAuth';

interface PostLoginMFAWrapperProps {
  children: React.ReactNode;
}

const PostLoginMFAWrapper: React.FC<PostLoginMFAWrapperProps> = ({ children }) => {
  const { mfaState, fetchMFAStatus, verifyMFALogin } = useMFA();
  const { authState, setAuthState, isAuthenticated } = useAuth();
  const { apiClient } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingMFA, setIsCheckingMFA] = useState(false);
  const [hasCheckedMFA, setHasCheckedMFA] = useState(false);
  const [isProcessingMagicLink, setIsProcessingMagicLink] = useState(false);

  // ALL HOOKS MUST BE CALLED FIRST - BEFORE ANY CONDITIONAL LOGIC

  // Dev-only bypass flag (env or URL): REACT_APP_DISABLE_MFA=true or ?mfa=off
  const urlParamsForBypass = new URLSearchParams(location.search);
  const disableMfaEnv = process.env.REACT_APP_DISABLE_MFA === 'true';
  const disableMfaUrl = urlParamsForBypass.get('mfa') === 'off';
  const disableMfa = disableMfaEnv || disableMfaUrl;

  // If URL has magic link params, we're entering via magic link - never show MFA screen, only loading until verified
  const hasMagicLinkParams = !!(urlParamsForBypass.get('token') && urlParamsForBypass.get('mfaType'));

  // 1. Check MFA requirements hook
  useEffect(() => {
    const checkMFARequirements = async () => {
      if (disableMfa) {
        debugAuthLog('[PostLoginMFA] Effect 1: skip (disableMfa), setHasCheckedMFA(true)');
        setHasCheckedMFA(true);
        return;
      }

      if (!isAuthenticated || !authState.token || hasCheckedMFA) {
        debugAuthLog('[PostLoginMFA] Effect 1: skip', { isAuthenticated: !!isAuthenticated, hasToken: !!authState.token, hasCheckedMFA });
        return;
      }

      debugAuthLog('[PostLoginMFA] Effect 1: fetching MFA status...');
      setIsCheckingMFA(true);
      try {
        await fetchMFAStatus();
        setHasCheckedMFA(true);
        debugAuthLog('[PostLoginMFA] Effect 1: fetchMFAStatus done, hasCheckedMFA=true');
      } catch (error) {
        setHasCheckedMFA(true);
        debugAuthLog('[PostLoginMFA] Effect 1: fetchMFAStatus error, hasCheckedMFA=true', error);
      } finally {
        setIsCheckingMFA(false);
      }
    };

    checkMFARequirements();
  }, [isAuthenticated, authState.token, hasCheckedMFA, fetchMFAStatus, disableMfa]);

  // 2. Handle magic link tokens hook
  useEffect(() => {
    const handleMagicLinkToken = async () => {
      if (disableMfa) return;
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');
      const mfaType = urlParams.get('mfaType');

      if (!token || !mfaType) {
        if (location.search) debugAuthLog('[PostLoginMFA] Effect 2: no token/mfaType in URL', { search: location.search });
        return;
      }
      if (!isAuthenticated || !authState.token) {
        debugAuthLog('[PostLoginMFA] Effect 2: have token+mfaType but not authenticated yet', { isAuthenticated, hasToken: !!authState.token });
        return;
      }

      debugAuthLog('[PostLoginMFA] Effect 2: verifying magic link token...');
      setIsProcessingMagicLink(true);
      try {
        const response = await verifyMFALogin({
          mfaType: mfaType as MFATypes.MFAType,
          token: token,
        });

        debugAuthLog('[PostLoginMFA] Effect 2: verifyMFALogin response', { success: response?.success });

        if (response.success) {
          try {
            sessionStorage.setItem('mfa_verified_token', authState.token || '');
            sessionStorage.setItem('mfa_verified_via_magic_link', '1');
            debugAuthLog('[PostLoginMFA] Effect 2: set mfa_verified_token + mfa_verified_via_magic_link, navigating to /');
          } catch {
            // ignore storage issues
          }
          navigate('/', { replace: true });
        } else {
          debugAuthLog('[PostLoginMFA] Effect 2: response.success false, not navigating');
          setIsProcessingMagicLink(false);
        }
      } catch (error) {
        debugAuthLog('[PostLoginMFA] Effect 2: verifyMFALogin error, navigating to /', error);
        navigate('/', { replace: true });
      } finally {
        setIsProcessingMagicLink(false);
      }
    };

    handleMagicLinkToken();
  }, [location.search, isAuthenticated, authState.token, verifyMFALogin, navigate, disableMfa]);

  // 3. Navigation logic hook
  useEffect(() => {
    if (disableMfa) return;
    if (
      hasCheckedMFA &&
      mfaState.status?.isEnforced &&
      (!mfaState.status.enabledMethods || mfaState.status.enabledMethods.length === 0)
    ) {
      navigate('/profile?tab=2fa');
    }
  }, [
    hasCheckedMFA,
    mfaState.status?.isEnforced,
    mfaState.status?.enabledMethods,
    navigate,
    disableMfa,
  ]);

  // 4. Create stable callback for verification success
  const handleVerificationSuccess = useCallback((verifyResponse?: any) => {
    try {
      if (verifyResponse?.fullAccessToken) {
        // MFA verification returned new full-access tokens — upgrade authState
        // so that subsequent API calls (loadGlobalData, etc.) use the full token.
        const newToken = verifyResponse.fullAccessToken;

        // Immediately update the API client so any API calls made before
        // the React effect fires already use the full-access token.
        apiClient.setAuthToken(newToken);

        setAuthState({
          ...authState,
          token: newToken,
          accessToken: newToken,
          refreshToken: verifyResponse.refreshToken || authState.refreshToken,
        });
        sessionStorage.setItem('mfa_verified_token', newToken);
      } else {
        // Fallback: tie verification to the current token
        sessionStorage.setItem('mfa_verified_token', authState.token || '');
      }
      // Clear MFA pending flag so loadGlobalData will run on next token change
      sessionStorage.removeItem('mfa_pending');
    } catch {
      // ignore storage issues
    }
    // After successful MFA verification, redirect to dashboard
    navigate('/', { replace: true });
  }, [navigate, authState, setAuthState, apiClient]);

  // ALL HOOKS ARE NOW DEFINED ABOVE - NO MORE HOOKS HERE

  const hasEnabledMethods = (mfaState.status?.enabledMethods?.length ?? 0) > 0;
  const mfaVerificationRequired =
    mfaState.status?.isRequired === true || hasEnabledMethods;
  let verifiedViaMagicLink = false;
  let verifiedToken: string | null = null;
  try {
    verifiedViaMagicLink = sessionStorage.getItem('mfa_verified_via_magic_link') === '1';
    verifiedToken = sessionStorage.getItem('mfa_verified_token');
  } catch {
    // ignore
  }
  const tokenMatch = verifiedToken && verifiedToken === (authState.token || '');

  debugAuthLog('[PostLoginMFA] render', {
    pathname: location.pathname,
    search: location.search,
    hasMagicLinkParams,
    isCheckingMFA,
    isProcessingMagicLink,
    hasCheckedMFA,
    mfaVerificationRequired,
    hasEnabledMethods,
    verifiedViaMagicLink,
    verifiedTokenLen: verifiedToken?.length ?? 0,
    authTokenLen: (authState.token || '').length,
    tokenMatch,
  });

  // If MFA bypass is active, render children immediately
  if (disableMfa) {
    debugAuthLog('[PostLoginMFA] return: disableMfa -> children');
    return <>{children}</>;
  }

  // If we're still checking MFA requirements, processing magic link, URL has magic link params,
  // OR authenticated but haven't checked MFA yet (initial render gap — prevents children from
  // firing API calls with a temporary token before we know MFA status)
  if (isCheckingMFA || isProcessingMagicLink || hasMagicLinkParams || (isAuthenticated && !hasCheckedMFA)) {
    debugAuthLog('[PostLoginMFA] return: loading (checking/processing/hasMagicLinkParams/initialCheck)');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isProcessingMagicLink || hasMagicLinkParams
              ? 'Verifying magic link...'
              : 'Checking security requirements...'}
          </p>
        </div>
      </div>
    );
  }

  // If MFA is required for this session (org/user required OR user has enabled methods), show verification screen
  if (hasCheckedMFA && mfaVerificationRequired) {
    if (verifiedViaMagicLink) {
      debugAuthLog('[PostLoginMFA] return: mfa required but verifiedViaMagicLink -> children (clearing flag)');
      sessionStorage.removeItem('mfa_verified_via_magic_link');
      return <>{children}</>;
    }
    if (tokenMatch) {
      debugAuthLog('[PostLoginMFA] return: mfa required but tokenMatch -> children');
      return <>{children}</>;
    }
    debugAuthLog('[PostLoginMFA] return: mfa required -> PostLoginMFAVerification');
    return <PostLoginMFAVerification onVerificationSuccess={handleVerificationSuccess} />;
  }

  // Don't render if we need to redirect
  if (
    hasCheckedMFA &&
    mfaState.status?.isEnforced &&
    (!mfaState.status.enabledMethods || mfaState.status.enabledMethods.length === 0)
  ) {
    debugAuthLog('[PostLoginMFA] return: redirect to profile 2fa');
    return null;
  }

  // If no MFA requirements or already verified, show the main content
  debugAuthLog('[PostLoginMFA] return: default -> children');
  return <>{children}</>;
};

export default PostLoginMFAWrapper;
